import { APIService } from '../../api/apiService';
import type { CollaboraEditorData, OfficeConfig } from '../../interfaces/OfficeConfig';

/** Map of file extension → Collabora editor URL (from discovery XML). */
export type CollaboraDiscoveryMap = Record<string, string>;

/** Cached discovery result so it's fetched only once per session. */
let cachedDiscovery: CollaboraDiscoveryMap | null = null;

/** Clears discovery cache (for unit tests only). */
export function resetCollaboraDiscoveryCacheForTests(): void {
  cachedDiscovery = null;
}

export class CollaboraService extends APIService {
  /**
   * Fetches the office provider config (anonymous, no auth).
   * GET /api/office/config
   */
  getOfficeConfig(): Promise<OfficeConfig> {
    return this.request('/office/config', 'GET', undefined, undefined, undefined, {
      skipAuth: true,
    });
  }

  /**
   * Gets Collabora editor launch data for an authenticated file.
   * GET /api/collabora/editor-url/{fileId}
   */
  getEditorUrl(fileId: number | string): Promise<CollaboraEditorData> {
    return this.request(`/collabora/editor-url/${fileId}`, 'GET');
  }

  /**
   * Gets ONLYOFFICE editor launch data for an authenticated file (WOPI mode).
   * Same WOPI launch shape as Collabora; the response carries onlyOfficeServerUrl.
   * GET /api/onlyoffice/editor-url/{fileId}
   */
  getOnlyOfficeEditorUrl(fileId: number | string): Promise<CollaboraEditorData> {
    return this.request(`/onlyoffice/editor-url/${fileId}`, 'GET');
  }

  /**
   * Gets OfficeGrant editor launch data for an authenticated file (WOPI mode).
   * GET /api/officegrant/editor-url/{fileId}
   */
  getOfficeGrantEditorUrl(fileId: number | string): Promise<CollaboraEditorData> {
    return this.request(`/officegrant/editor-url/${fileId}`, 'GET');
  }

  /**
   * Gets Collabora editor launch data for a public file (anonymous).
   * GET /api/collabora/editor-url/public/{token}?childFileId={id}
   */
  getPublicEditorUrl(
    publicFileToken: string,
    childFileId?: number,
    accessCode?: string,
  ): Promise<CollaboraEditorData> {
    const qs = childFileId != null ? `?childFileId=${childFileId}` : '';
    const url = `/collabora/editor-url/public/${publicFileToken}${qs}`;
    if (accessCode) {
      return this.request(url, 'POST', { accessCode }, undefined, undefined, { skipAuth: true });
    }
    return this.request(url, 'GET', undefined, undefined, undefined, { skipAuth: true });
  }

  /**
   * Fetches the Collabora discovery XML and parses it into a map of
   * file extension → editor URL. Cached after first successful fetch.
   *
   * @param collaboraServerUrl Base URL of the Collabora Online server
   */
  async getDiscoveryMap(collaboraServerUrl: string): Promise<CollaboraDiscoveryMap> {
    if (cachedDiscovery) return cachedDiscovery;

    const discoveryUrl = `${collaboraServerUrl}/hosting/discovery`;
    const response = await fetch(discoveryUrl);
    if (!response.ok) {
      throw new Error(
        `Collabora discovery failed: ${response.status} ${response.statusText} (${discoveryUrl})`,
      );
    }
    const xmlText = await response.text();

    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, 'text/xml');
    const actions = doc.querySelectorAll('action');

    const map: CollaboraDiscoveryMap = {};
    actions.forEach((action) => {
      const ext = action.getAttribute('ext');
      const urlsrc = action.getAttribute('urlsrc');
      if (ext && urlsrc) {
        map[ext.toLowerCase()] = urlsrc;
      }
    });

    cachedDiscovery = map;
    return map;
  }

  /**
   * Builds the full editor URL for a given file extension and WOPI source.
   *
   * @param discoveryMap Parsed discovery map from getDiscoveryMap
   * @param fileExtension File extension (e.g. "docx", "xlsx")
   * @param wopiSrc WOPI source URL from editor-url response
   * @param readOnly When true, append Collabora `permission=readonly` (load-time view-only)
   * @returns Full editor URL ready for form POST, or null if extension not supported
   */
  buildEditorUrl(
    discoveryMap: CollaboraDiscoveryMap,
    fileExtension: string,
    wopiSrc: string,
    readOnly = false,
  ): string | null {
    const ext = fileExtension.toLowerCase().replace('.', '');
    const urlsrc = discoveryMap[ext];
    if (!urlsrc) return null;
    const url = `${urlsrc}WOPISrc=${encodeURIComponent(wopiSrc)}`;
    return readOnly ? `${url}&permission=readonly` : url;
  }
}
